import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, Field, NSelect, useFetch, V_STATUS } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VehicleTable } from "@/pages/flotte/AllVehicles";
import { api, errMsg, fDate, todayIso } from "@/lib/api";

const EMPTY = { plate: "", brand: "", model: "", engine: "Diesel", first_registration: "", type: "livraison", status: "actif", mileage: 0, financing: "", contract_km: "", contract_end: "" };
const ENGINES = ["Diesel", "Essence", "Hybride", "Hybride rechargeable", "Électrique", "GPL"].map((e) => ({ value: e, label: e }));

function VehicleDialog({ v, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...v });
  const [busy, setBusy] = useState(false);
  const set = (k) => (x) => setF({ ...f, [k]: x?.target ? x.target.value : x });
  const save = async () => {
    setBusy(true);
    const body = { ...f, mileage: Number(f.mileage) || 0, contract_km: f.contract_km ? Number(f.contract_km) : null,
      first_registration: f.first_registration || null, contract_end: f.contract_end || null, financing: f.financing || null };
    try {
      if (v.id) await api.put(`/vehicles/${v.id}`, body);
      else await api.post("/vehicles", body);
      toast.success("Véhicule enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">{v.id ? "Modifier le véhicule" : "Nouveau véhicule"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Immatriculation"><Input value={f.plate} onChange={set("plate")} className="font-mono uppercase" data-testid="vehicle-plate-input" /></Field>
          <Field label="Type"><NSelect value={f.type} onChange={set("type")} testid="vehicle-type-select" options={[{ value: "livraison", label: "Livraison" }, { value: "commercial", label: "Commercial" }]} /></Field>
          <Field label="Marque"><Input value={f.brand} onChange={set("brand")} data-testid="vehicle-brand-input" /></Field>
          <Field label="Modèle"><Input value={f.model} onChange={set("model")} data-testid="vehicle-model-input" /></Field>
          <Field label="Motorisation"><NSelect value={f.engine} onChange={set("engine")} testid="vehicle-engine-select" options={ENGINES} /></Field>
          <Field label="Mise en circulation"><Input type="date" value={f.first_registration || ""} onChange={set("first_registration")} data-testid="vehicle-reg-input" /></Field>
          <Field label="Kilométrage"><Input type="number" value={f.mileage} onChange={set("mileage")} data-testid="vehicle-mileage-input" /></Field>
          <Field label="Statut"><NSelect value={f.status} onChange={set("status")} testid="vehicle-status-select" options={Object.entries(V_STATUS).filter(([k]) => k !== "archive").map(([k, x]) => ({ value: k, label: x[0] }))} /></Field>
          <Field label="Financement"><Input value={f.financing || ""} onChange={set("financing")} placeholder="LLD, LOA, achat…" data-testid="vehicle-financing-input" /></Field>
          <Field label="Km contractuel"><Input type="number" value={f.contract_km || ""} onChange={set("contract_km")} data-testid="vehicle-contract-km-input" /></Field>
          <Field label="Fin de contrat"><Input type="date" value={f.contract_end || ""} onChange={set("contract_end")} data-testid="vehicle-contract-end-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="vehicle-save-button">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ v, onClose, onSaved }) {
  const role = v.type === "livraison" ? "chauffeur" : "commercial";
  const users = useFetch("/users", { role });
  const hist = useFetch(`/vehicles/${v.id}`);
  const [uid, setUid] = useState(v.assigned_user_id || "");
  const [date, setDate] = useState(todayIso());
  const save = async () => {
    try {
      await api.post(`/vehicles/${v.id}/assign`, { user_id: uid || null, date });
      toast.success("Affectation enregistrée");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">Affectation — {v.plate}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={role === "chauffeur" ? "Chauffeur habituel" : "Commercial"}>
            <NSelect value={uid} onChange={setUid} placeholder="Aucun (désaffecter)" testid="assign-user-select" options={(users.data || []).map((u) => ({ value: u.id, label: u.name }))} />
          </Field>
          <Field label="Date d'effet"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="assign-date-input" /></Field>
        </div>
        <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mt-2">Historique des affectations</div>
        <ul className="text-sm space-y-1 max-h-40 overflow-y-auto" data-testid="assign-history">
          {(hist.data?.assignments || []).map((a) => <li key={a.id}>{a.user_name} — {fDate(a.start_date)} → {a.end_date ? fDate(a.end_date) : "en cours"}</li>)}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="assign-save-button">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveDialog({ v, onClose, onSaved }) {
  const demo = v === "demo";
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(demo ? "/vehicles/archive-demo" : `/vehicles/${v.id}/archive`);
      toast.success(demo ? `${data.archived} véhicule(s) DÉMO archivé(s)` : `${v.plate} archivé`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="vehicle-archive-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">{demo ? "Archiver tous les véhicules DÉMO" : `Archiver ${v.plate}`}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          {demo ? "Seuls les véhicules marqués DÉMO sont concernés ; aucun véhicule réel n'est modifié." : "Le véhicule sera retiré des listes courantes."} Les contrôles, anomalies, entretiens et affectations sont conservés et restent consultables (filtre « Archivés »). L'affectation en cours est clôturée.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={go} disabled={busy} className="bg-[#0E2D43] text-white" data-testid="vehicle-archive-confirm">Archiver</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VehiclesAdmin() {
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(null);
  const [archive, setArchive] = useState(null);
  const demoLeft = useFetch("/demo/status");
  const done = () => {
    setEdit(null);
    setAssign(null);
    setArchive(null);
    VehicleTable.reload?.();
    demoLeft.reload();
  };
  return (
    <div data-testid="vehicles-admin-page">
      <PageHeader overline="Paramètres" title="Référentiel véhicules">
        {demoLeft.data?.active && <Button variant="outline" onClick={() => setArchive("demo")} data-testid="archive-demo-vehicles-button">Archiver les véhicules DÉMO</Button>}
      </PageHeader>
      <VehicleTable manage onEdit={setEdit} onAssign={setAssign} onArchive={setArchive} onCreate={() => setEdit({})} />
      {archive && <ArchiveDialog v={archive} onClose={() => setArchive(null)} onSaved={done} />}
      {edit && <VehicleDialog v={edit} onClose={() => setEdit(null)} onSaved={done} />}
      {assign && <AssignDialog v={assign} onClose={() => setAssign(null)} onSaved={done} />}
    </div>
  );
}
